/**
 * InfinitSales - Onboarding Page
 * Multi-step onboarding flow
 *
 * Flow: Register → ChannelSelection → [WA Business Tutorial] → ConnectionSetup → AgentOnboarding → Inbox
 *
 * When user selects wa_business, a tutorial step is shown before proceeding to connection.
 * User must scroll to the bottom to enable the "Prosseguir" button.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  ChevronLeft,
  Building2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Smartphone,
  MessageSquare as MessageSquareIcon,
  History,
  Shield,
  ExternalLink,
  ChevronRight,
  ChevronDown,
  Info,
  ArrowRight,
  ArrowLeft,
  Copy,
  Check,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { ChannelSelectionStep } from '../../components/onboarding';
import onboardingService from '../../services/global/onboardingService';

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { colors } = useTheme();

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Tutorial step state
  const [showTutorial, setShowTutorial] = useState(false);
  const [pendingChannel, setPendingChannel] = useState(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const tutorialContentRef = useRef(null);

  // Track scroll position to enable the proceed button
  const handleTutorialScroll = useCallback(() => {
    const el = tutorialContentRef.current;
    if (!el) return;
    const threshold = 100;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    if (isAtBottom) {
      setHasScrolledToBottom(true);
    }
  }, []);

  // Check on mount if content doesn't need scrolling
  useEffect(() => {
    if (!showTutorial) return;
    const el = tutorialContentRef.current;
    if (!el) return;
    // If content fits without scrolling, enable button immediately
    if (el.scrollHeight <= el.clientHeight + 100) {
      setHasScrolledToBottom(true);
    }
  }, [showTutorial]);

  const handleCopyCode = (text) => {
    navigator.clipboard.writeText(text);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2500);
  };

  // Handle channel selection
  const handleChannelSelect = async (channel) => {
    if (channel === 'wa_business') {
      // Show tutorial first
      setPendingChannel(channel);
      setShowTutorial(true);
      setHasScrolledToBottom(false);
      return;
    }

    // For other channels, proceed directly
    await proceedToConnection(channel);
  };

  // Proceed to connection setup (after tutorial or directly for non-business)
  const proceedToConnection = async (channel) => {
    setLoading(true);
    setError('');

    try {
      const response = await onboardingService.selectChannel(channel);

      if (response.data?.user) {
        updateUser(response.data.user);
      }

      navigate('/connection-setup', {
        state: { channel },
        replace: true
      });

    } catch (err) {
      console.error('Channel selection error:', err);
      setError(
        err.response?.data?.detail ||
        'Erro ao configurar canal. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle proceed from tutorial
  const handleTutorialProceed = () => {
    if (pendingChannel) {
      proceedToConnection(pendingChannel);
    }
  };

  // Handle back from tutorial
  const handleTutorialBack = () => {
    setShowTutorial(false);
    setPendingChannel(null);
    setHasScrolledToBottom(false);
  };

  // ============================================
  // STYLES
  // ============================================
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.bgSecondary,
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
    },
    content: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 24px',
    },
    stepContainer: {
      width: '100%',
      maxWidth: '900px',
    },
    error: {
      padding: '12px 16px',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '10px',
      color: '#ef4444',
      fontSize: '14px',
      marginBottom: '24px',
      textAlign: 'center',
      maxWidth: '500px',
      margin: '0 auto 24px',
    },
    // Tutorial styles
    tutorialContainer: {
      minHeight: '100vh',
      backgroundColor: colors.bgSecondary,
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
    },
    tutorialHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 24px',
      backgroundColor: colors.bgPrimary,
      borderBottom: `1px solid ${colors.borderColor}`,
      minHeight: '56px',
      flexShrink: 0,
    },
    tutorialHeaderLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    tutorialBackBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '36px',
      height: '36px',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '8px',
      cursor: 'pointer',
    },
    tutorialContent: {
      flex: 1,
      overflowY: 'auto',
      padding: '24px',
    },
    tutorialInner: {
      maxWidth: '720px',
      margin: '0 auto',
    },
    section: {
      backgroundColor: colors.bgPrimary,
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '14px',
      padding: '24px',
      marginBottom: '20px',
    },
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '18px',
      fontWeight: '600',
      color: colors.textPrimary,
      marginBottom: '16px',
    },
    paragraph: {
      fontSize: '14px',
      lineHeight: '1.7',
      color: colors.textSecondary,
      marginBottom: '12px',
    },
    divider: {
      height: '1px',
      backgroundColor: colors.borderColor,
      margin: '20px 0',
    },
    stepNumber: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      borderRadius: '50%',
      fontSize: '14px',
      fontWeight: 700,
      marginRight: '12px',
      flexShrink: 0,
    },
    stepTitle: {
      fontSize: '16px',
      fontWeight: 600,
      color: colors.textPrimary,
      display: 'flex',
      alignItems: 'center',
      marginBottom: '10px',
    },
    stepContent: {
      paddingLeft: '40px',
      marginBottom: '16px',
    },
    imageContainer: {
      position: 'relative',
      borderRadius: '12px',
      overflow: 'hidden',
      border: `2px solid ${colors.borderColor}`,
      marginBottom: '16px',
      backgroundColor: colors.bgTertiary,
    },
    image: {
      width: '100%',
      display: 'block',
      borderRadius: '10px',
    },
    imageCaption: {
      fontSize: '12px',
      color: colors.textTertiary,
      textAlign: 'center',
      padding: '8px',
      fontStyle: 'italic',
    },
    optionCard: (type) => ({
      display: 'flex',
      alignItems: 'flex-start',
      gap: '14px',
      padding: '16px',
      borderRadius: '12px',
      marginBottom: '12px',
      border: type === 'correct'
        ? '2px solid #25D366'
        : `1px solid ${colors.borderColor}`,
      backgroundColor: type === 'correct'
        ? '#25D36608'
        : colors.bgTertiary,
      opacity: type === 'wrong' ? 0.7 : 1,
    }),
    optionIcon: (type) => ({
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      backgroundColor: type === 'correct' ? '#25D36620' : `${colors.danger || '#ef4444'}15`,
    }),
    optionBadge: (type) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '11px',
      fontWeight: 700,
      padding: '3px 10px',
      borderRadius: '20px',
      marginTop: '6px',
      backgroundColor: type === 'correct' ? '#25D36620' : `${colors.danger || '#ef4444'}15`,
      color: type === 'correct' ? '#25D366' : (colors.danger || '#ef4444'),
    }),
    warningBox: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '16px',
      borderRadius: '12px',
      backgroundColor: '#f59e0b10',
      border: '1px solid #f59e0b30',
      marginBottom: '16px',
    },
    warningText: {
      fontSize: '14px',
      color: '#f59e0b',
      lineHeight: 1.6,
      margin: 0,
    },
    infoBox: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '16px',
      borderRadius: '12px',
      backgroundColor: '#3b82f610',
      border: '1px solid #3b82f630',
      marginBottom: '16px',
    },
    infoText: {
      fontSize: '14px',
      color: '#3b82f6',
      lineHeight: 1.6,
      margin: 0,
    },
    successBox: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '16px',
      borderRadius: '12px',
      backgroundColor: '#25D36610',
      border: '1px solid #25D36630',
      marginBottom: '16px',
    },
    successText: {
      fontSize: '14px',
      color: '#25D366',
      lineHeight: 1.6,
      margin: 0,
    },
    featureGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '12px',
      marginBottom: '16px',
    },
    featureCard: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px 14px',
      borderRadius: '10px',
      backgroundColor: colors.bgSecondary,
      border: `1px solid ${colors.borderColor}`,
    },
    featureIcon: {
      width: '32px',
      height: '32px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    featureText: {
      fontSize: '13px',
      color: colors.textSecondary,
      lineHeight: 1.4,
    },
    // Footer with proceed button
    tutorialFooter: {
      padding: '16px 24px',
      backgroundColor: colors.bgPrimary,
      borderTop: `1px solid ${colors.borderColor}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      flexShrink: 0,
    },
    proceedBtn: (enabled) => ({
      padding: '14px 32px',
      backgroundColor: enabled ? '#25D366' : colors.bgTertiary,
      color: enabled ? 'white' : colors.textTertiary,
      border: 'none',
      borderRadius: '12px',
      fontSize: '15px',
      fontWeight: 600,
      cursor: enabled ? 'pointer' : 'not-allowed',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      transition: 'all 0.2s',
      opacity: loading ? 0.7 : 1,
    }),
    scrollHint: {
      fontSize: '13px',
      color: colors.textTertiary,
      fontStyle: 'italic',
    },
  };

  // ============================================
  // TUTORIAL RENDER
  // ============================================
  if (showTutorial) {
    return (
      <div style={styles.tutorialContainer}>
        {/* Header */}
        <div style={styles.tutorialHeader}>
          <div style={styles.tutorialHeaderLeft}>
            <button style={styles.tutorialBackBtn} onClick={handleTutorialBack}>
              <ArrowLeft size={20} />
            </button>
            <div style={{
              width: '36px',
              height: '36px',
              backgroundColor: '#25D36615',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Building2 size={18} color="#25D366" />
            </div>
            <div>
              <h2 style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
                Tutorial: conectar WhatsApp Business
              </h2>
              <p style={{ fontSize: '12px', color: colors.textSecondary, margin: 0 }}>
                Passo a passo da conexão via Facebook Business
              </p>
            </div>
          </div>
          {/* Botão "Avançar": pula direto pro fim do tutorial sem precisar ler tudo */}
          <button
            onClick={() => {
              if (tutorialContentRef.current) {
                tutorialContentRef.current.scrollTo({
                  top: tutorialContentRef.current.scrollHeight,
                  behavior: 'smooth',
                });
              }
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '999px',
              border: `1px solid ${colors.borderColor}`,
              backgroundColor: 'transparent',
              color: colors.textSecondary,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = colors.accentPrimary;
              e.currentTarget.style.color = colors.accentPrimary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.borderColor;
              e.currentTarget.style.color = colors.textSecondary;
            }}
            title="Pular para o final do tutorial"
          >
            Avançar
            <ChevronDown size={14} />
          </button>
        </div>

        {/* Scrollable content */}
        <div
          ref={tutorialContentRef}
          style={styles.tutorialContent}
          onScroll={handleTutorialScroll}
        >
          <div style={styles.tutorialInner}>

            {/* Introdução enxuta — sem o warningBox laranja gigante. Resumo de
                1 frase + breve lembrete inline sobre a conta do Facebook. */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <Building2 size={18} color={colors.accentPrimary} />
                Como funciona a conexão
              </div>

              <p style={styles.paragraph}>
                A InfinitSales usa o <strong style={{ color: colors.textPrimary }}>modo de coexistência</strong> do
                WhatsApp Business — você continua usando o app no celular normalmente e a plataforma
                envia/recebe mensagens pela mesma conta, sem precisar desconectar de lugar nenhum.
              </p>

              <p style={{ ...styles.paragraph, fontSize: '13px', color: colors.textTertiary }}>
                💡 No login do Facebook, use a <strong>mesma conta vinculada ao seu Business Manager</strong> do
                WhatsApp Business — usar uma conta diferente impede a conexão.
              </p>

              {/* Benefícios */}
              <div style={styles.featureGrid}>
                <div style={styles.featureCard}>
                  <div style={{ ...styles.featureIcon, backgroundColor: '#25D36615' }}>
                    <Smartphone size={18} color="#25D366" />
                  </div>
                  <span style={styles.featureText}>
                    Continue usando o app no celular
                  </span>
                </div>
                <div style={styles.featureCard}>
                  <div style={{ ...styles.featureIcon, backgroundColor: '#3b82f615' }}>
                    <MessageSquareIcon size={18} color="#3b82f6" />
                  </div>
                  <span style={styles.featureText}>
                    Mensagens sincronizadas em tempo real
                  </span>
                </div>
                <div style={styles.featureCard}>
                  <div style={{ ...styles.featureIcon, backgroundColor: '#f59e0b15' }}>
                    <History size={18} color="#f59e0b" />
                  </div>
                  <span style={styles.featureText}>
                    Importa até 180 dias de histórico
                  </span>
                </div>
                <div style={styles.featureCard}>
                  <div style={{ ...styles.featureIcon, backgroundColor: '#8b5cf615' }}>
                    <Shield size={18} color="#8b5cf6" />
                  </div>
                  <span style={styles.featureText}>
                    API oficial da Meta, sem risco de bloqueio
                  </span>
                </div>
              </div>
            </div>

            {/* Pré-requisito */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <Smartphone size={18} color={colors.accentPrimary} />
                Pré-requisito: WhatsApp Business instalado
              </div>

              <p style={styles.paragraph}>
                Certifique-se de que o aplicativo <strong style={{ color: colors.textPrimary }}>WhatsApp Business</strong> está
                instalado e funcionando no seu celular. O modo de coexistência só funciona com o
                WhatsApp Business (ícone com "B"), e não com o WhatsApp comum.
              </p>

              {/* Screenshots lado a lado */}
              <div style={{
                display: 'flex',
                gap: '16px',
                marginBottom: '16px',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}>
                <div style={{ ...styles.imageContainer, maxWidth: '200px', flex: '0 1 200px' }}>
                  <img
                    src="/part_intro_app_business.jpeg"
                    alt="WhatsApp Business instalado no celular"
                    style={styles.image}
                  />
                  <div style={styles.imageCaption}>
                    App instalado no celular
                  </div>
                </div>
                <div style={{ ...styles.imageContainer, maxWidth: '200px', flex: '0 1 200px' }}>
                  <img
                    src="/part_intro_app_business_opened.jpeg"
                    alt="WhatsApp Business aberto e funcionando"
                    style={styles.image}
                  />
                  <div style={styles.imageCaption}>
                    App aberto e funcionando
                  </div>
                </div>
              </div>
            </div>

            {/* A opção correta */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <ChevronRight size={18} color={colors.accentPrimary} />
                Qual opção selecionar no popup do Meta
              </div>

              <div style={styles.warningBox}>
                <AlertTriangle size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
                <p style={styles.warningText}>
                  <strong>Atenção:</strong> O popup do Facebook mostrará várias opções.
                  É muito importante que você selecione <strong>apenas a opção correta</strong> indicada abaixo.
                  As outras opções podem causar problemas na sua conta.
                </p>
              </div>

              {/* Screenshot do modal */}
              <div style={styles.imageContainer}>
                <img
                  src="/modal_meta_connect_app.jpeg"
                  alt="Modal do Meta mostrando a opção correta"
                  style={styles.image}
                />
                <div style={styles.imageCaption}>
                  Popup do Meta — selecione a opção destacada
                </div>
              </div>

              {/* Opção CORRETA */}
              <div style={styles.optionCard('correct')}>
                <div style={styles.optionIcon('correct')}>
                  <CheckCircle2 size={20} color="#25D366" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: 600, color: colors.textPrimary }}>
                    "Conectar a um app do WhatsApp Business"
                  </div>
                  <p style={{ fontSize: '13px', color: colors.textSecondary, margin: '4px 0 0', lineHeight: 1.5 }}>
                    Esta é a opção que conecta sua conta em <strong>modo de coexistência</strong>.
                    Seu WhatsApp Business no celular continuará funcionando normalmente.
                  </p>
                  <div style={styles.optionBadge('correct')}>
                    <CheckCircle2 size={12} />
                    SELECIONE ESTA OPÇÃO
                  </div>
                </div>
              </div>

              {/* Opção ERRADA 1 */}
              <div style={styles.optionCard('wrong')}>
                <div style={styles.optionIcon('wrong')}>
                  <XCircle size={20} color={colors.danger || '#ef4444'} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: 600, color: colors.textPrimary, opacity: 0.7 }}>
                    "Inserir um novo número de telefone"
                  </div>
                  <p style={{ fontSize: '13px', color: colors.textTertiary, margin: '4px 0 0', lineHeight: 1.5 }}>
                    Pode exigir desconectar o WhatsApp de outros lugares e o app no celular
                    pode parar de funcionar.
                  </p>
                  <div style={styles.optionBadge('wrong')}>
                    <XCircle size={12} />
                    NÃO SELECIONE
                  </div>
                </div>
              </div>

              {/* Opção ERRADA 2 */}
              <div style={styles.optionCard('wrong')}>
                <div style={styles.optionIcon('wrong')}>
                  <XCircle size={20} color={colors.danger || '#ef4444'} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: 600, color: colors.textPrimary, opacity: 0.7 }}>
                    "Selecionar um número existente"
                  </div>
                  <p style={{ fontSize: '13px', color: colors.textTertiary, margin: '4px 0 0', lineHeight: 1.5 }}>
                    Pode exigir desconexão do provedor anterior e o histórico não será importado.
                  </p>
                  <div style={styles.optionBadge('wrong')}>
                    <XCircle size={12} />
                    NÃO SELECIONE
                  </div>
                </div>
              </div>

              <div style={styles.infoBox}>
                <Info size={20} color="#3b82f6" style={{ flexShrink: 0, marginTop: '2px' }} />
                <p style={styles.infoText}>
                  Se você não encontrar a opção "Conectar a um app do WhatsApp Business",
                  certifique-se de que o <strong>WhatsApp Business</strong> está instalado e
                  atualizado no seu celular.
                </p>
              </div>
            </div>

            {/* Resumo */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <CheckCircle2 size={18} color="#25D366" />
                Resumo
              </div>

              <div style={styles.successBox}>
                <CheckCircle2 size={20} color="#25D366" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <p style={styles.successText}>
                    <strong>Ao clicar em "Prosseguir para conexão"</strong>, o popup do Facebook/Meta
                    será aberto. Lembre-se:
                  </p>
                  <ul style={{ ...styles.successText, paddingLeft: '20px', marginTop: '8px' }}>
                    <li>Faça login com a conta do Facebook correta (que possui o portfólio)</li>
                    <li>Selecione <strong>"Conectar a um app do WhatsApp Business"</strong></li>
                    <li>Escaneie o QR Code no app do celular</li>
                    <li>Confirme a vinculação</li>
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Footer with proceed button */}
        <div style={styles.tutorialFooter}>
          {!hasScrolledToBottom && (
            <span style={styles.scrollHint}>
              Role até o final para habilitar o botão
            </span>
          )}
          <button
            style={styles.proceedBtn(hasScrolledToBottom && !loading)}
            onClick={handleTutorialProceed}
            disabled={!hasScrolledToBottom || loading}
          >
            {loading ? (
              <>Conectando...</>
            ) : (
              <>
                Prosseguir para conexão
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // CHANNEL SELECTION RENDER (default)
  // ============================================
  return (
    <div style={styles.container}>
      <main style={styles.content}>
        <div style={styles.stepContainer}>
          {error && <div style={styles.error}>{error}</div>}
          <ChannelSelectionStep
            onSelect={handleChannelSelect}
            loading={loading}
          />
        </div>
      </main>
    </div>
  );
};

export default Onboarding;
